//
// Copyright 2025 DXOS.org
//

import { Atom, Registry } from '@effect-atom/atom';
import * as Schema from 'effect/Schema';
import type * as Types from 'effect/Types';

import { Format, Obj, Type, View } from '@dxos/echo';
import { TypeEnum, formatToType, typeToFormat } from '@dxos/echo/Format';
import { createSchemaReference, getSchemaReference } from '@dxos/echo/internal';
import { type JsonSchema as JsonSchemaType } from '@dxos/echo/JsonSchema';
import { type Mutable } from '@dxos/echo/Obj';
import { SchemaEx } from '@dxos/effect';
import { invariant } from '@dxos/invariant';
import { log } from '@dxos/log';
import { omit, pick } from '@dxos/util';

import { makeMultiSelectAnnotations, makeSingleSelectAnnotations } from '../util';
import { PropertySchema, type PropertyType } from './format';

export const VIEW_FIELD_LIMIT = 32;

/**
 * Composite of view and schema metadata for a property.
 */
export type FieldProjection = {
  field: View.FieldType;
  props: PropertyType;
};

/**
 * Callback type for wrapping mutations in Obj.update().
 * Contains separate callbacks for projection and schema mutations
 * since Obj.update() cannot be nested.
 * Note: Callbacks return void because Obj.update() returns void.
 * Uses Mutable<T> to allow mutations within the callbacks since schemas are readonly by default.
 */
export type ProjectionChangeCallback = {
  /** Callback to wrap projection mutations. */
  projection: (mutate: (mutableProjection: Mutable<View.Projection>) => void) => void;
  /** Callback to wrap schema mutations. */
  schema: (mutate: (mutableSchema: Types.DeepMutable<JsonSchemaType>) => void) => void;
};

/**
 * Creates a change callback for ECHO-backed View and Type.Type objects.
 * Use this when the view is stored in the ECHO database.
 *
 * @param view - The ECHO-backed view object.
 * @param schema - Optional persisted `Type.AnyEntity`. If not provided, schema mutations will throw.
 */
export const createEchoChangeCallback = (view: View.View, schema?: Type.AnyEntity): ProjectionChangeCallback => ({
  // Inside Obj.update, v is Mutable<View.View>, so v.projection is already mutable.
  projection: (mutate) => Obj.update(view, (view) => mutate(view.projection as Mutable<View.Projection>)),
  schema:
    schema == null
      ? () => {
          throw new Error('Schema is not mutable');
        }
      : (mutate) => Type.update(schema, (draft) => mutate(draft.jsonSchema)),
});

/**
 * Creates a change callback that directly mutates objects without wrapping.
 * Use this for plain JavaScript objects (tests, non-ECHO scenarios).
 * Cast to mutable types since these are non-ECHO objects that can be directly mutated.
 */
export const createDirectChangeCallback = (
  projection: View.Projection,
  schema: Types.DeepMutable<JsonSchemaType>,
): ProjectionChangeCallback => ({
  projection: (mutate) => mutate(projection as Mutable<View.Projection>),
  schema: (mutate) => mutate(schema),
});

/**
 * Props for creating a ProjectionModel.
 */
export type ProjectionModelProps = {
  /** Registry for atom management. Optional - if not provided, atoms won't be created. */
  registry?: Registry.Registry;
  /** The View object (for subscriptions). */
  view: View.View;
  /** The base JSON schema of the data being projected. */
  baseSchema: JsonSchemaType;
  /**
   * Callbacks to wrap mutations in Obj.update().
   * Use createEchoChangeCallback() for ECHO-backed objects or createDirectChangeCallback() for plain objects.
   */
  change: ProjectionChangeCallback;
};

/**
 * Wrapper for Projection that manages Field and Format updates.
 * Exposes atoms for reactive access.
 */
export class ProjectionModel {
  private readonly _encode = Schema.encodeSync(PropertySchema);
  private readonly _decode = Schema.decodeSync(PropertySchema, {});

  private readonly _registry: Registry.Registry;
  private readonly _view: View.View;
  private readonly _baseSchema: JsonSchemaType;
  private readonly _change: ProjectionChangeCallback;

  // Internal atoms.
  private readonly _viewAtom: Atom.Atom<Obj.Snapshot<View.View>>;
  private readonly _projectionAtom: Atom.Atom<View.Projection>;
  private readonly _fieldsAtom: Atom.Atom<readonly View.FieldType[]>;
  private readonly _hiddenFieldsAtom: Atom.Atom<readonly View.FieldType[]>;
  private readonly _allFieldsAtom: Atom.Atom<readonly View.FieldType[]>;

  constructor({ registry = Registry.make(), view, baseSchema, change }: ProjectionModelProps) {
    this._registry = registry;
    this._view = view;
    this._baseSchema = baseSchema;
    this._change = change;

    this._viewAtom = Obj.atom(this._view);

    // Derived atom that extracts projection from the view snapshot.
    this._projectionAtom = Atom.make((get) => {
      const view = get(this._viewAtom);
      return view.projection;
    });

    // Derived atoms for filtered fields.
    this._fieldsAtom = Atom.make((get) => {
      const projection = get(this._projectionAtom);
      return projection.fields.filter((field) => field.visible !== false);
    });

    this._hiddenFieldsAtom = Atom.make((get) => {
      const projection = get(this._projectionAtom);
      return projection.fields.filter((field) => field.visible === false);
    });

    this._allFieldsAtom = Atom.make((get) => {
      const projection = get(this._projectionAtom);
      return projection.fields;
    });
  }

  //
  // Atom getters for reactive access
  //

  /**
   * Atom for the visible fields in the projection.
   */
  get fields(): Atom.Atom<readonly View.FieldType[]> {
    return this._fieldsAtom;
  }

  /**
   * Atom for the hidden fields in the projection.
   */
  get hiddenFields(): Atom.Atom<readonly View.FieldType[]> {
    return this._hiddenFieldsAtom;
  }

  /**
   * Atom for all fields in the projection (both visible and hidden).
   */
  get allFields(): Atom.Atom<readonly View.FieldType[]> {
    return this._allFieldsAtom;
  }

  /**
   * Atom for the core projection data type.
   */
  get projection(): Atom.Atom<View.Projection> {
    return this._projectionAtom;
  }

  //
  // Synchronous getters for imperative access
  //

  /**
   * Gets the current visible fields array.
   */
  getFields(): readonly View.FieldType[] {
    return this._registry.get(this._fieldsAtom);
  }

  /**
   * Gets the current hidden fields array.
   */
  getHiddenFields(): readonly View.FieldType[] {
    return this._registry.get(this._hiddenFieldsAtom);
  }

  /**
   * Gets the current all fields array.
   */
  getAllFields(): readonly View.FieldType[] {
    return this._registry.get(this._allFieldsAtom);
  }

  /**
   * Gets the current projection value.
   */
  getProjection(): View.Projection {
    return this._registry.get(this._projectionAtom);
  }

  /**
   * The base schema of the data being projected.
   */
  get baseSchema() {
    return this._baseSchema;
  }

  /**
   * Construct a new property.
   */
  createFieldProjection(): View.FieldType {
    invariant(this._view.projection.fields.length < VIEW_FIELD_LIMIT, `Field limit reached: ${VIEW_FIELD_LIMIT}`);

    const field: View.FieldType = {
      id: View.createFieldId(),
      path: createUniqueProperty(this._view.projection),
      visible: true,
    };

    log('createFieldProjection', { field });
    this._change.projection((projection) => {
      projection.fields.push(field);
    });
    return field;
  }

  getFieldId(path: string): string | undefined {
    return this._view.projection.fields.find((field) => field.path === path)?.id;
  }

  /**
   * Get projection of View fields and JSON schema property annotations.
   */
  getFieldProjection(fieldId: string): FieldProjection {
    invariant(this._baseSchema.properties);
    const field = this._view.projection.fields.find((field) => field.id === fieldId);
    invariant(field, `invalid field: ${fieldId}`);
    invariant(field.path.indexOf('.') === -1);

    const jsonProperty: JsonSchemaType = this._baseSchema.properties[field.path] ?? { format: Format.TypeFormat.None };
    const { type: schemaType, format: schemaFormat = Format.TypeFormat.None, annotations, ...rest } = jsonProperty;

    const unwrappedProperty =
      'allOf' in jsonProperty && jsonProperty.allOf?.length ? jsonProperty.allOf[0] : jsonProperty;
    const { typename: referenceSchema } = getSchemaReference(unwrappedProperty) ?? {};
    const type = referenceSchema ? TypeEnum.Ref : (schemaType as TypeEnum);

    const format =
      (() => {
        if (referenceSchema) {
          return Format.TypeFormat.Ref;
        } else if (schemaFormat === Format.TypeFormat.None) {
          return typeToFormat[type];
        } else {
          return schemaFormat as Format.TypeFormat;
        }
      })() ?? Format.TypeFormat.None;

    const getOptions = () => {
      if (format === Format.TypeFormat.SingleSelect) {
        return annotations?.meta?.singleSelect?.options;
      }
      if (format === Format.TypeFormat.MultiSelect) {
        return annotations?.meta?.multiSelect?.options;
      }
    };

    const options = getOptions();

    const values: typeof PropertySchema.Type = {
      type,
      format,
      property: field.path as SchemaEx.JsonProp,
      referenceSchema,
      referencePath: field.referencePath,
      options,
      ...rest,
    };

    const props = values.type ? this._decode(values) : values;
    log('getFieldProjection', { field, props });
    return { field, props };
  }

  /**
   * Get projection of View fields and JSON schema property annotations, returning undefined if field doesn't exist.
   * @param fieldId The ID of the field to get projection for
   */
  tryGetFieldProjection(fieldId: string): FieldProjection | undefined {
    invariant(this._baseSchema.properties);
    const field = this._view.projection.fields.find((field) => field.id === fieldId);
    if (!field) {
      return undefined;
    }

    return this.getFieldProjection(fieldId);
  }

  /**
   * Get all field projections.
   */
  getFieldProjections(): FieldProjection[] {
    return this._view.projection.fields.map((field) => this.getFieldProjection(field.id));
  }

  /**
   * Identifies schema properties not visible in the current view projection.
   * @returns Schema property names that aren't mapped to any view field path, returned as an alphabetically sorted string array.
   */
  getHiddenProperties(): string[] {
    return this.getHiddenFields().map((field) => field.path as string) ?? [];
  }

  /**
   * Hides a field from the view by setting its visible flag to false.
   */
  hideFieldProjection(fieldId: string): void {
    this._change.projection((projection) => {
      const field = projection.fields.find((field) => field.id === fieldId);
      if (field) {
        field.visible = false;
      }
    });
  }

  showFieldProjection(property: SchemaEx.JsonProp): void {
    invariant(this._baseSchema.properties);
    invariant(property in this._baseSchema.properties);

    this._change.projection((projection) => {
      const existingField = projection.fields.find((field) => field.path === property);
      if (existingField) {
        existingField.visible = true;
      } else {
        projection.fields.unshift({
          id: View.createFieldId(),
          path: property,
          visible: true,
        } satisfies View.FieldType);
      }
    });
  }

  /**
   * Update JSON schema property annotations and view fields.
   * @param projection The field and props to update
   * @param index Optional index for inserting new fields. Ignored when updating existing fields.
   */
  setFieldProjection({ field, props }: Partial<FieldProjection>, index?: number): void {
    log('setFieldProjection', { field, props, index });

    const sourcePropertyName = field?.path;
    const targetPropertyName = props?.property;
    const hasSourceAndTarget = !!sourcePropertyName && !!targetPropertyName;
    const isRename = hasSourceAndTarget && sourcePropertyName !== targetPropertyName;

    // Update projection fields.
    if (field || targetPropertyName) {
      this._change.projection((projection) => {
        if (targetPropertyName) {
          const existingField = projection.fields.find((f) => f.path === targetPropertyName);
          if (existingField && existingField.visible === false) {
            existingField.visible = true;
          }
        }

        // TODO(burdon): Set field if does not exist.
        if (field) {
          const propsValues = props ? (pick(props, ['referencePath']) as Partial<View.FieldType>) : undefined;
          const clonedField: View.FieldType = { ...field, ...propsValues };
          const fieldIndex = projection.fields.findIndex((f) => f.path === sourcePropertyName);
          if (fieldIndex === -1) {
            invariant(projection.fields.length < VIEW_FIELD_LIMIT, `Field limit reached: ${VIEW_FIELD_LIMIT}`);
            if (index !== undefined && index >= 0 && index <= projection.fields.length) {
              projection.fields.splice(index, 0, clonedField);
            } else {
              projection.fields.push(clonedField);
            }
          } else {
            const existing = projection.fields[fieldIndex];
            const updatedField: View.FieldType = {
              ...existing,
              ...clonedField,
              ...(isRename ? { path: targetPropertyName } : undefined),
            };
            projection.fields[fieldIndex] = updatedField;
          }
        }
      });
    }

    // Update schema properties.
    if (props) {
      let { property, type, format, referenceSchema, options, ...rest }: Partial<PropertyType> = this._encode(
        omit(props, ['referencePath']),
      );
      invariant(property);
      invariant(format);

      const jsonProperty: Mutable<JsonSchemaType> = {};

      if (referenceSchema) {
        Object.assign(jsonProperty, createSchemaReference(referenceSchema));
        return;
      } else if (format) {
        type = formatToType[format];
      }

      if (options) {
        if (format === Format.TypeFormat.SingleSelect) {
          makeSingleSelectAnnotations(jsonProperty, options);
        }

        if (format === Format.TypeFormat.MultiSelect) {
          makeMultiSelectAnnotations(jsonProperty, options);
        }
      }

      invariant(type !== TypeEnum.Ref);
      this._change.schema((baseSchema) => {
        baseSchema.properties ??= {};
        // Type assertion needed: Property values from the readonly schema may contain
        // readonly nested types, but we're in a mutation context.
        baseSchema.properties[property!] = {
          type,
          format,
          ...jsonProperty,
          ...rest,
        } as Types.DeepMutable<JsonSchemaType>;
        if (isRename) {
          delete baseSchema.properties[sourcePropertyName!];

          // Update propertyOrder array if it exists
          if (baseSchema.propertyOrder) {
            const orderIndex = baseSchema.propertyOrder.indexOf(sourcePropertyName!);
            if (orderIndex !== -1) {
              baseSchema.propertyOrder[orderIndex] = property!;
            }
          }

          // Update required array if it exists
          if (baseSchema.required) {
            const requiredIndex = baseSchema.required.indexOf(sourcePropertyName!);
            if (requiredIndex !== -1) {
              baseSchema.required[requiredIndex] = property!;
            }
          }
        }
      });
    }
  }

  /**
   * Delete a field from the view and return the deleted projection for potential undo.
   */
  deleteFieldProjection(fieldId: string): { deleted: FieldProjection; index: number } {
    // NOTE(ZaymonFC): We need to clone this because the underlying object is going to be modified.
    const current = this.getFieldProjection(fieldId);
    invariant(current, `Field projection not found for fieldId: ${fieldId}`);

    const snapshot = JSON.parse(JSON.stringify(current)) as FieldProjection;

    // Calculate field index before deleting (Obj.update returns void, so we can't get return values from the callback).
    const fieldIndex = this._view.projection.fields.findIndex((field) => field.id === fieldId);

    // Delete field from projection.
    this._change.projection((projection) => {
      const idx = projection.fields.findIndex((field) => field.id === fieldId);
      if (idx !== -1) {
        projection.fields.splice(idx, 1);
      }
    });

    // Delete property from schema.
    this._change.schema((baseSchema) => {
      invariant(baseSchema.properties, 'Schema properties must exist');
      invariant(snapshot.field.path, 'Field path must exist');
      delete baseSchema.properties[snapshot.field.path];

      // Check that it's actually gone.
      invariant(!baseSchema.properties[snapshot.field.path], 'Field path still exists');
    });

    return { deleted: snapshot, index: fieldIndex };
  }

  /**
   * Normalizes the view by:
   * 1. Removing fields that no longer exist in the schema
   * 2. Adding missing schema properties as hidden fields
   */
  normalizeView(): void {
    // Get all properties from the schema.
    const schemaProperties = new Set(Object.keys(this._baseSchema.properties ?? {}));

    this._change.projection((projection) => {
      // 1. Remove fields that don't exist in schema anymore.
      for (let i = projection.fields.length - 1; i >= 0; i--) {
        const field = projection.fields[i];
        if (!schemaProperties.has(field.path)) {
          projection.fields.splice(i, 1);
        }
      }

      // 2. Find schema properties not represented in any field.
      const fieldPaths = new Set(projection.fields.map((field) => field.path));

      // 3. Add missing schema properties as hidden fields (excluding 'id').
      for (const prop of schemaProperties) {
        if (prop !== 'id' && !fieldPaths.has(prop as SchemaEx.JsonProp)) {
          // Add new hidden field.
          projection.fields.push({
            id: View.createFieldId(),
            path: prop as SchemaEx.JsonProp,
            visible: false,
          });
        }
      }
    });
  }
}

export const createUniqueProperty = (projection: View.Projection): SchemaEx.JsonProp => {
  let n = 1;
  while (true) {
    const property: SchemaEx.JsonProp = `prop_${n++}` as SchemaEx.JsonProp;
    const idx = projection.fields.findIndex((field) => field.path === property);
    if (idx === -1) {
      return property;
    }
  }
};
